import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getInventoryItems, getInventoryLocations } from '../../utils/inventoryApi';
import { createVendor, listVendors } from '../../utils/vendorsApi';
import {
  createPurchaseOrder,
  getPurchaseOrder,
  listPurchaseOrders,
  receivePurchaseOrderLineItem,
  submitPurchaseOrder,
  cancelPurchaseOrder,
} from '../../utils/purchaseOrdersApi';
import type { InventoryItem, InventoryLocation, PurchaseOrder, Vendor } from '../../types/inventory';
import { CreatePOModal } from './components/CreatePOModal';
import { ReceivePOModal } from './components/ReceivePOModal';
import { CreateVendorModal } from './components/CreateVendorModal';

export const PurchaseOrders: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);
  const [receivingPo, setReceivingPo] = useState<PurchaseOrder | null>(null);
  const [isLoadingPoDetail, setIsLoadingPoDetail] = useState(false);

  const vendorById = useMemo(() => {
    const m = new Map<string, Vendor>();
    vendors.forEach((v) => m.set(v.id, v));
    return m;
  }, [vendors]);

  const fetchAll = async () => {
    if (!practiceId) return;
    setLoading(true);
    setError(null);
    try {
      const [pos, vends, locs, items] = await Promise.all([
        listPurchaseOrders(practiceId, { status: filterStatus === 'all' ? undefined : filterStatus, search: searchTerm || undefined }),
        listVendors(practiceId),
        getInventoryLocations(practiceId),
        getInventoryItems(practiceId),
      ]);
      setPurchaseOrders(pos);
      setVendors(vends);
      setLocations(locs);
      setInventoryItems(items);
    } catch (e: any) {
      setError(e?.message || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!practiceId) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return;
    const t = setTimeout(() => fetchAll(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, searchTerm]);

  if (!practiceId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-gray-600">Select a practice to view purchase orders.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-sm text-gray-600">Loading purchase orders…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search PO number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="PARTIAL_RECEIVED">Partial</option>
            <option value="RECEIVED">Received</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateVendorModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Add Vendor
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Purchase Order
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO Number</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Delivery</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {purchaseOrders.map((po) => (
              <tr key={po.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{po.po_number}</div>
                  <div className="text-xs text-gray-500">{po.id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {vendorById.get(po.vendor_id)?.name || po.vendor_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{po.order_date}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{po.expected_delivery_date || '—'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {po.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${Number(po.total_cost || 0).toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                  {po.status === 'DRAFT' && (
                    <button
                      onClick={async () => {
                        await submitPurchaseOrder(practiceId, po.id);
                        await fetchAll();
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Submit
                    </button>
                  )}
                  {po.status !== 'CANCELLED' && po.status !== 'RECEIVED' && (
                    <button
                      onClick={async () => {
                        await cancelPurchaseOrder(practiceId, po.id);
                        await fetchAll();
                      }}
                      className="text-red-600 hover:text-red-900"
                    >
                      Cancel
                    </button>
                  )}
                  {(po.status === 'SUBMITTED' || po.status === 'CONFIRMED' || po.status === 'PARTIAL_RECEIVED' || po.status === 'DRAFT') && (
                    <button
                      onClick={async () => {
                        setIsLoadingPoDetail(true);
                        try {
                          const full = await getPurchaseOrder(practiceId, po.id);
                          setReceivingPo(full);
                        } finally {
                          setIsLoadingPoDetail(false);
                        }
                      }}
                      className="text-green-600 hover:text-green-900"
                    >
                      {isLoadingPoDetail ? 'Loading…' : 'Receive'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {purchaseOrders.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                  No purchase orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreatePOModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        vendors={vendors}
        locations={locations}
        inventoryItems={inventoryItems}
        onCreate={async (payload) => {
          await createPurchaseOrder(practiceId, payload);
          await fetchAll();
        }}
      />

      <CreateVendorModal
        isOpen={showCreateVendorModal}
        onClose={() => setShowCreateVendorModal(false)}
        onCreate={async (payload) => {
          await createVendor(practiceId, payload);
          await fetchAll();
        }}
      />

      {receivingPo && (
        <ReceivePOModal
          isOpen={!!receivingPo}
          onClose={() => setReceivingPo(null)}
          purchaseOrder={receivingPo}
          onReceive={async (payload) => {
            await receivePurchaseOrderLineItem(practiceId, receivingPo.id, payload);
            const updated = await getPurchaseOrder(practiceId, receivingPo.id);
            setReceivingPo(updated);
            await fetchAll();
          }}
        />
      )}
    </div>
  );
};


